/**
 * useRequestInvite
 *
 * TanStack Query mutation hook for requesting invite codes
 *
 * API: POST /api/v1/credentialing/request-access
 *
 * Request body:
 * - companyName: string
 * - email: string
 * - phone: string
 * - county: string
 * - serviceTypes: string[]
 *
 * Response:
 * - requestId: string
 * - message: string
 * - estimatedReviewTime: string
 */

import { useMutation } from '@tanstack/react-query';
import { apiClient } from '../../../shared/api/client';

interface RequestInviteData {
  companyName: string;
  email: string;
  phone: string;
  county: string;
  serviceTypes: string[];
}

interface RequestInviteResponse {
  requestId: string;
  message: string;
  estimatedReviewTime: string;
}

export function useRequestInvite() {
  return useMutation({
    mutationFn: async (data: RequestInviteData): Promise<RequestInviteResponse> => {
      const { data: result, error } = await apiClient.POST('/api/v1/credentialing/request-access', {
        body: {
          companyName: data.companyName,
          email: data.email,
          phone: data.phone,
          county: data.county,
          serviceTypes: data.serviceTypes,
        },
      });

      if (error) {
        const errorMessage = error.error?.message || 'Failed to submit request';
        throw new Error(errorMessage);
      }

      return result as RequestInviteResponse;
    },
  });
}
